import { Box, Container, Grid, Typography, useTheme, useMediaQuery } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CameraAlt,
  Campaign,
  Brush,
  Language,
  School,
  Facebook,
  Instagram,
  YouTube,
  LinkedIn,
} from '@mui/icons-material';

const ServiceCard = ({ icon: Icon, title, description, delay }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: isMobile ? 0 : delay }}
      viewport={{ once: true, margin: '-50px' }}
    >
      <Box
        sx={{
          height: '100%',
          p: { xs: 3, md: 4 },
          borderRadius: { xs: 3, md: 4 },
          background: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            transform: { xs: 'translateY(-5px)', md: 'translateY(-10px)' },
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            background: 'rgba(255, 255, 255, 0.05)',
            borderColor: theme.palette.primary.main,
            '& .service-icon': {
              transform: 'scale(1.1) rotate(5deg)',
            },
          },
        }}
      >
        <Box
          className="service-icon"
          sx={{
            display: 'inline-flex',
            p: { xs: 1.5, md: 2 },
            borderRadius: 2,
            mb: { xs: 2, md: 3 },
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            boxShadow: '0 8px 16px rgba(255, 107, 0, 0.2)',
            alignSelf: 'flex-start',
            transition: 'transform 0.3s ease',
          }}
        >
          <Icon sx={{ 
            fontSize: { xs: 28, md: 32 }, 
            color: 'white',
          }} />
        </Box>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: { xs: 1.5, md: 2 },
            color: 'rgba(180, 180, 180, 1)',
            fontWeight: 600,
            fontSize: { xs: '1.25rem', md: '1.5rem' },
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'rgba(160, 160, 160, .8)',
            fontSize: { xs: '0.9rem', md: '1rem' },
            lineHeight: 1.6,
            flex: 1,
          }}
        >
          {description}
        </Typography>
      </Box>
    </motion.div>
  );
};

const Services = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const services = [
    {
      icon: Facebook,
      title: 'Social Media Management',
      description: 'Expert management of your social media presence across Facebook, Instagram, LinkedIn, and YouTube to engage your audience and drive growth.',
    },
    {
      icon: Campaign,
      title: 'Creative Ad Campaigns',
      description: 'Stand out from the crowd with innovative social media and outdoor marketing campaigns that capture attention and deliver results.',
    },
    {
      icon: Brush,
      title: 'Graphic Design',
      description: 'Professional design services for flyers, invitations, logos, and all your visual branding needs, crafted to perfection.',
    },
    {
      icon: CameraAlt,
      title: 'Photography & Videography',
      description: 'High-quality product photography and comprehensive event coverage to showcase your brand in the best light.',
    },
    {
      icon: School,
      title: 'Educational Opportunities',
      description: 'Assistance with applications for national and international educational opportunities, including schools, work, training, and conferences.',
    },
    {
      icon: Language,
      title: 'Document Translation',
      description: 'Professional translation services between French, Kirundi, and English, ensuring accurate and culturally appropriate translations.',
    },
  ];

  return (
    <Box
      id="services"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: '#121212',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url("/Nuyu-media/Nuyu Pattern_1.png")',
          backgroundRepeat: 'repeat',
          opacity: 0.02,
          zIndex: 1,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{ 
              mb: { xs: 4, md: 8 },
              color: 'rgba(200, 200, 200, 1)',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              letterSpacing: '-0.02em',
            }}
          >
            Our Services
          </Typography>
        </motion.div>

        <Grid 
          container 
          spacing={{ xs: 2.5, md: 4 }}
          sx={{
            width: '100%',
            margin: '0 auto',
          }}
        >
          <AnimatePresence>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <ServiceCard
                  icon={service.icon}
                  title={service.title}
                  description={service.description}
                  delay={index * 0.1}
                />
              </Grid>
            ))}
          </AnimatePresence>
        </Grid>
      </Container>
    </Box>
  );
};

export default Services;
